// Passos 1 a 3 da trava: escolher a unidade, medir o impacto, decidir.
//
// É a parte que pode parar averbação legítima, e o desenho todo sai daí:
//
//  - O ESTADO APARECE ANTES DE MEDIR. `travaAtiva` vem na lista de unidades, e
//    o operador vê o panorama sem gastar uma medição.
//  - A MEDIÇÃO NÃO DISPARA SOZINHA. `enabled: false` e `refetch()` no botão. A
//    versão anterior media ao SELECIONAR a unidade, e o prazo de 10 minutos
//    começava a correr antes de o operador ler o número.
//  - O PRAZO É VISÍVEL. Deriva de `medido_em`, não do render.
//  - DESLIGAR NÃO DEPENDE DE NADA DO SERVIDOR. Nem medição, nem fonte, nem
//    estado de query. É a válvula de escape da ADR-0005, e a única coisa que se
//    exige é a justificativa — que o próprio operador digita.

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, KpiCard, Modal, Pill, TextareaField } from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { QUERY_UNIDADES_OPERAVEIS, listarUnidadesOperaveis } from "../../lib/unidades-operaveis";
import { podeAcessar, readAverbadoraPermissoesFromJwt } from "../../lib/averbadora-perms";
import {
  estadoDaValidade,
  formatarRestante,
  frasearDivergencia,
  traduzirErroDaTrava,
} from "../../lib/trava-margem-erros";
import { SeletorDeUnidade, type Unidade } from "./trava-margem-unidades";

const brl = (n: number | null | undefined) =>
  n == null ? "—" : n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const quando = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");

export interface TravaMargemDecisaoProps {
  unidadeId: number | null;
  onUnidade: (id: number | null) => void;
}

export function TravaMargemDecisao({ unidadeId, onUnidade }: TravaMargemDecisaoProps) {
  const qc = useQueryClient();
  const [justificativa, setJustificativa] = useState("");
  const [confirmando, setConfirmando] = useState<"ligar" | "desligar" | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);

  // Permissão lida uma vez: sem `folhas`, o caminho de ativação já nasce
  // indisponível, com o motivo — em vez de falhar só no envio.
  const permissoes = useMemo(() => {
    try {
      const bruto = window.localStorage.getItem("atlas:tokens");
      return readAverbadoraPermissoesFromJwt(
        bruto ? (JSON.parse(bruto) as { access_token?: string }).access_token : null,
      );
    } catch {
      return null;
    }
  }, []);
  const podeOperar = podeAcessar(permissoes, "folhas");

  const prefeituras = useQuery({
    queryKey: QUERY_UNIDADES_OPERAVEIS,
    queryFn: listarUnidadesOperaveis,
  });
  const unidades = (prefeituras.data?.prefeituras ?? []) as unknown as Unidade[];
  const unidade = unidades.find((u) => u.id === unidadeId) ?? null;

  // A MEDIÇÃO SÓ RODA QUANDO PEDIDA. `enabled: false` é o ponto: com
  // `enabled: unidadeId != null` — como era antes — o relógio de validade
  // começava a correr no instante em que o operador escolhia a unidade, antes
  // de ele ter lido qualquer número.
  const medicao = useQuery({
    queryKey: ["admin", "trava", "impacto", unidadeId],
    queryFn: () => atlas.admin.trava.impacto(unidadeId!),
    enabled: false,
    gcTime: 0,
    staleTime: 0,
  });
  const m = medicao.data;

  // Tick de 1s só enquanto há medição na tela: é o que faz o prazo ANDAR à
  // vista, em vez de o operador descobrir a expiração ao ser recusado.
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    if (!m?.medido_em) return;
    const id = window.setInterval(() => setAgora(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [m?.medido_em]);

  const validade = estadoDaValidade(m?.medido_em, agora);
  const medicaoValida = m?.medicao_valida === true;
  const fonteAutoritativa = m?.fonte === "postgres";
  const jaLigada = unidade?.travaAtiva === true || m?.trava_ativa === true;
  const justificativaOk = justificativa.trim().length >= 3;

  /** Por que ligar está indisponível — associado ao botão, não solto na tela. */
  const motivoNaoPodeLigar = (): string | null => {
    if (!podeOperar) return "Seu usuário não tem a permissão de folhas.";
    if (unidadeId == null) return "Escolha a unidade pagadora.";
    if (jaLigada) return "A trava desta unidade já está ligada.";
    if (!m) return "Consulte o impacto antes de ligar.";
    if (!medicaoValida) return "A medição desta unidade não voltou válida.";
    if (!fonteAutoritativa) return "A medição precisa vir do PostgreSQL.";
    if (validade.estado === "expirada") return "A medição expirou. Consulte o impacto de novo.";
    if (!justificativaOk) return "Escreva a justificativa (mínimo 3 caracteres).";
    return null;
  };
  const impedimentoLigar = motivoNaoPodeLigar();

  /**
   * Por que desligar está indisponível.
   *
   * Repare no que NÃO está aqui: medição, fonte, validade, estado de query.
   * Desligar é a válvula de escape — só depende da unidade, da permissão e de
   * um texto que o operador digita.
   */
  const impedimentoDesligar = !podeOperar
    ? "Seu usuário não tem a permissão de folhas."
    : unidadeId == null
      ? "Escolha a unidade pagadora."
      : !justificativaOk
        ? "Escreva a justificativa (mínimo 3 caracteres)."
        : null;

  const definir = useMutation({
    mutationFn: (ativa: boolean) =>
      atlas.admin.trava.definir(unidadeId!, {
        ativa,
        confirmar: true,
        justificativa: justificativa.trim(),
        ...(ativa ? { vinculosBloqueadosNaAtivacao: m?.vinculos_com_pendencia, medidoEm: m?.medido_em } : {}),
      }),
    onSuccess: (_r, ativa) => {
      setErro(null);
      setConfirmando(null);
      setSucesso(
        ativa
          ? `Trava LIGADA em ${unidade?.nome ?? unidadeId}. ${m?.vinculos_com_pendencia ?? 0} vínculo(s) passam a ser recusados.`
          : `Trava DESLIGADA em ${unidade?.nome ?? unidadeId}. A unidade volta a averbar normalmente.`,
      );
      setJustificativa("");
      void qc.invalidateQueries({ queryKey: ["admin", "trava"] });
      void qc.invalidateQueries({ queryKey: ["admin", "prefeituras"] });
    },
    onError: (e: unknown) => {
      setSucesso(null);
      setConfirmando(null);
      setErro(e);
    },
  });

  const erroTraduzido = erro ? traduzirErroDaTrava(erro) : null;
  const divergencia = erro ? frasearDivergencia(erro) : null;

  return (
    <div style={{ display: "grid", gap: 20 }}>
      {/* ---------- 1. unidade ---------- */}
      <Card>
        <h2 style={{ margin: "0 0 4px", fontSize: 16 }}>1. Unidade pagadora</h2>
        <SeletorDeUnidade
          unidades={unidades}
          carregando={prefeituras.isLoading}
          valor={unidadeId}
          onChange={(id) => {
            onUnidade(id);
            // Estado da decisão anterior não sobrevive à troca de unidade — nem
            // o erro, nem o sucesso, nem a medição (`queryKey` inclui o id).
            setConfirmando(null);
            setErro(null);
            setSucesso(null);
          }}
        />
      </Card>

      {/* ---------- 2. impacto ---------- */}
      <Card>
        <h2 style={{ margin: "0 0 8px", fontSize: 16 }}>2. Medir o impacto</h2>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <Button onClick={() => void medicao.refetch()} disabled={unidadeId == null || medicao.isFetching}>
            {medicao.isFetching ? "Medindo…" : "Consultar impacto"}
          </Button>

          {unidade && (
            <span style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 13 }}>
              Estado agora:{" "}
              <Pill variant={unidade.travaAtiva ? "rejeitada" : "emdia"}>
                {unidade.travaAtiva ? "LIGADA" : "desligada"}
              </Pill>
            </span>
          )}

          {/* O PRAZO, à vista. */}
          {m?.medido_em && validade.estado !== "sem-medicao" && (
            <span
              role="status"
              style={{
                fontSize: 13,
                color:
                  validade.estado === "expirada"
                    ? "var(--danger-500)"
                    : validade.estado === "expirando"
                      ? "var(--warn-500)"
                      : "var(--text-muted)",
              }}
            >
              {validade.estado === "expirada"
                ? "Medição expirada — consulte o impacto de novo antes de ligar."
                : `Medição válida por mais ${formatarRestante(validade.restanteMs)}.`}
            </span>
          )}
        </div>

        {m && !medicaoValida && (
          <p role="alert" style={{ color: "var(--danger-500)", marginBottom: 0 }}>
            Não foi possível medir esta unidade{m.erro ? ` — ${m.erro}` : ""}. Ligar fica indisponível;{" "}
            <strong>desligar continua funcionando</strong>.
            {Array.isArray(m.filiais) && m.filiais.length > 0 && (
              <>
                {" "}
                Esta organização processa a folha de outras: escolha qual folha medir.
                <span style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                  {m.filiais.map((f) => (
                    <Button key={f.id} variant="ghost" size="sm" onClick={() => onUnidade(f.id)}>
                      {f.nome}
                    </Button>
                  ))}
                </span>
              </>
            )}
          </p>
        )}

        {m && medicaoValida && (
          <>
            <div
              style={{
                display: "grid",
                gap: 12,
                gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                marginTop: 16,
              }}
            >
              <KpiCard
                label="Vínculos que seriam bloqueados"
                value={m.vinculos_com_pendencia ?? "—"}
                accent={(m.vinculos_com_pendencia ?? 0) > 0 ? "warn" : "success"}
                hint={`de ${m.vinculos_total ?? "—"} na unidade`}
              />
              <KpiCard label="Valor pendente" value={brl(m.valor_pendente_total)} accent="info" />
              <KpiCard label="Competência" value={m.competencia_mais_recente ?? "—"} />
              <KpiCard label="Medida em" value={quando(m.medido_em)} hint={`fonte: ${m.fonte}`} />
            </div>
            {m.distribuicao_por_situacao && (
              <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 0 }}>
                Por situação:{" "}
                {Object.entries(m.distribuicao_por_situacao)
                  .map(([s, n]) => `${s}: ${n}`)
                  .join(" · ") || "—"}
              </p>
            )}
          </>
        )}
      </Card>

      {/* ---------- 3. decisão ---------- */}
      <Card>
        <h2 style={{ margin: "0 0 8px", fontSize: 16 }}>3. Decidir</h2>

        <TextareaField
          label="Justificativa"
          required
          hint="Obrigatória nos dois sentidos. Fica na trilha de auditoria e é o que responde “por quê” quando a consignatária perguntar."
          value={justificativa}
          onChange={(e) => setJustificativa(e.target.value)}
          placeholder="ex.: piloto da autarquia, catálogo 100% classificado"
          rows={2}
          maxLength={500}
        />

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
          <Button
            disabled={impedimentoLigar != null || definir.isPending}
            aria-describedby={impedimentoLigar ? "motivo-ligar" : undefined}
            onClick={() => setConfirmando("ligar")}
          >
            Ligar a trava desta unidade
          </Button>
          <Button
            variant="ghost"
            disabled={impedimentoDesligar != null || definir.isPending}
            aria-describedby={impedimentoDesligar ? "motivo-desligar" : undefined}
            onClick={() => setConfirmando("desligar")}
          >
            Desligar
          </Button>
        </div>

        {/* O motivo mora ao lado do botão e é referenciado por ele: um controle
            cinza sem explicação associada é o que o leitor de tela não anuncia. */}
        {impedimentoLigar && (
          <p id="motivo-ligar" style={{ color: "var(--text-muted)", fontSize: 13, margin: "8px 0 0" }}>
            Ligar indisponível: {impedimentoLigar}
          </p>
        )}
        {impedimentoDesligar && (
          <p id="motivo-desligar" style={{ color: "var(--text-muted)", fontSize: 13, margin: "4px 0 0" }}>
            Desligar indisponível: {impedimentoDesligar}
          </p>
        )}

        {sucesso && (
          <p role="status" style={{ color: "var(--success)", marginBottom: 0 }}>
            {sucesso}
          </p>
        )}

        {erroTraduzido && (
          <div
            role="alert"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 8,
              border: `1px solid ${erroTraduzido.naoEhRecusaDaTrava ? "var(--warn-500)" : "var(--danger-500)"}`,
              background: "color-mix(in srgb, var(--danger-500) 8%, transparent)",
            }}
          >
            <strong>{erroTraduzido.causa}</strong>
            {divergencia && <div style={{ marginTop: 4 }}>{divergencia}</div>}
            <div style={{ marginTop: 4 }}>{erroTraduzido.proximaAcao}</div>
            {erroTraduzido.detalheTecnico && (
              <div style={{ marginTop: 6, fontSize: 12, color: "var(--text-dim)" }}>
                Detalhe: {erroTraduzido.detalheTecnico}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ---------- confirmações ---------- */}
      <Modal
        open={confirmando === "ligar"}
        onClose={() => setConfirmando(null)}
        title={`Ligar a trava em ${unidade?.nome ?? ""}?`}
        danger
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmando(null)}>
              Cancelar
            </Button>
            <Button onClick={() => definir.mutate(true)} disabled={definir.isPending}>
              {definir.isPending ? "Ativando…" : "Confirmo, ligar"}
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          <strong>{m?.vinculos_com_pendencia ?? 0}</strong> de <strong>{m?.vinculos_total ?? 0}</strong>{" "}
          vínculo(s) passarão a ser recusados, somando <strong>{brl(m?.valor_pendente_total)}</strong> em
          pendências.
        </p>
        <p style={{ margin: 0, color: "var(--text-muted)" }}>
          A partir de agora a consignatária recebe <code>margem_pendente_classificacao</code> em vez de um número de
          margem para esses vínculos, em todos os canais. Contratos já averbados continuam intactos.
        </p>
      </Modal>

      <Modal
        open={confirmando === "desligar"}
        onClose={() => setConfirmando(null)}
        title={`Desligar a trava em ${unidade?.nome ?? ""}?`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmando(null)}>
              Cancelar
            </Button>
            <Button onClick={() => definir.mutate(false)} disabled={definir.isPending}>
              {definir.isPending ? "Desligando…" : "Confirmo, desligar"}
            </Button>
          </>
        }
      >
        {/* Esta confirmação não consulta, não aguarda e não valida nada: é uma
            pergunta ao operador. Qualquer leitura aqui recriaria o portão que a
            ADR-0005 removeu de propósito. */}
        <p style={{ margin: 0 }}>
          A unidade volta a receber margem normalmente. Pendências continuam sendo medidas — apenas deixam de
          bloquear.
        </p>
      </Modal>
    </div>
  );
}
